import React, { useState} from 'react'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteSharpIcon from '@mui/icons-material/DeleteSharp';

import Tooltip from "@mui/material/Tooltip";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import classnames from "classnames";
import CustomCurrencyInput from "../../components/CustomCurrencyInput";
import useEnterEffect from "../../hooks/useEnterEffect";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EditLocationIcon from '@mui/icons-material/EditLocation';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import {LocationModal} from "../DealPage/LocationModal";
import {useUserContext} from "../../contexts/UserContext";
import {PopoverInput} from "../../components/PopoverInput";
import {Link} from "react-router-dom";
import {DeskingModal} from "./DeskingModal";
import {DealModal} from "./DealModal";
import {useFetchHook} from "../../hooks/useFetchHook";
import fetcher from "../../fetcher";
import {usePricingsStore} from "../../stores/pricingsStore";
import {useParams} from "react-router";
import {useModalsStore} from "../../stores/modalsStore";
import {useShallow} from "zustand/react/shallow";
import _ from "lodash";
import {buildLocationString} from "../../utils/utils";
import {useDefaultQuoteSettingsContext} from "../../contexts/DefaultQuoteSettingsContext";
import useDidMountEffect from "../../hooks/useDidMountEffect";

const getInitialState = (pricing, trim, quoteSettings) => {
    const customerLocation = quoteSettings.customer_location
    const dealerLocation = quoteSettings.dealer_location

    return {
        description: pricing?.description || '',
        msrp: pricing?.msrp || '',
        invoice: pricing?.invoice || null,
        above: pricing?.above || 0,
        back: pricing?.back || 0,
        profit: pricing?.profit || quoteSettings.profit,
        mileage: quoteSettings.mileage,
        mileage_alt: quoteSettings.mileage_alt,

        drive_off: quoteSettings.drive_off,
        car_id: trim.id,
        base_car_id: trim.id,
        base_car: trim.id,
        is_purchase: false,
        pricing_id: pricing?.id,
        pricing: pricing?.id,
        option_code_ids: [],
        rebates: [],

        address_line_one: customerLocation.address_line_one,
        address_line_two: customerLocation.address_line_two,
        city: customerLocation.city,
        state: customerLocation.state,
        zipcode: customerLocation.zipcode,
        county: customerLocation.county,

        dealer_address_line_one: dealerLocation.address_line_one,
        dealer_address_line_two: dealerLocation.address_line_two,
        dealer_city: dealerLocation.city,
        dealer_state: dealerLocation.state,
        dealer_zipcode: dealerLocation.zipcode,
        dealer_county: dealerLocation.county,
    }
}

export const PricingListRow = ( { trim, pricing } ) => {
    const { quoteSettings } = useDefaultQuoteSettingsContext()
    const params = useParams();
    const default_location = quoteSettings.customer_location
    const [dealLocation, setDealLocation] = useState(default_location)

    const [pricingState, setPricingState] = useState(getInitialState(pricing, trim, quoteSettings))
    const [isCreatingLease, setIsCreatingLease] = useState(false)
    const [isCreatingPurchase, setIsCreatingPurchase] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [isUpdating, setIsUpdating] = useState(false)

    const fetchPricings = usePricingsStore((state) => state.fetchPricings)

    const [createPricingResponse, createPricing] = useFetchHook(fetcher.createPricing)
    const [deletePricingResponse, deletePricing] = useFetchHook(fetcher.deletePricing)
    const [updatePricingResponse, updatePricing] = useFetchHook(fetcher.updatePricing)
    const [createDealResponse, createDeal] = useFetchHook(fetcher.createDeal)

    const {
        openModal,
        closeModalById,
    } = useModalsStore(
        useShallow((state) => ({
            openModal: state.openModal,
            closeModalById: state.closeModalById,
        }))
    )

    const saveLocation = (location, shouldSetCustomerLocation, shouldSetDealerLocation) => {
        let updateObj = {}

        if (shouldSetCustomerLocation) {
            updateObj = {
                location: location,
                location_id: location.id,
                address_line_one: location.address_line_one,
                address_line_two: location.address_line_two,
                city: location.city,
                state: location.state,
                zipcode: location.zipcode,
                county: location.county,
            }
        }
        if (shouldSetDealerLocation) {
            updateObj = {
                ...updateObj,
                dealer_location: location,
                dealer_location_id: location.id,
                dealer_address_line_one: location.address_line_one,
                dealer_address_line_two: location.address_line_two,
                dealer_city: location.city,
                dealer_state: location.state,
                dealer_zipcode: location.zipcode,
                dealer_county: location.county
            }
        }
        if (!_.isEmpty(updateObj)) {
            setDealLocation(location)
            setPricingState({...pricingState, ...updateObj})
        }

        closeModalById("LocationModal")
    }

    const updatePricingState = (field, value) => {
        setPricingState({...pricingState, [field]: value})
    }

    const deletePricingRow = (pricingId) => {
        setIsDeleting(true)
        return deletePricing(pricingId).then(() => {
            setIsDeleting(false)
            fetchPricings(params.make)
        }).catch(() => {
            setIsDeleting(false)
        })
    }

    const hasPricingChanged = () => {
        return (
            pricingState.description !== pricing.description ||
            pricingState.msrp !== pricing.msrp ||
            pricingState.invoice !== pricing.invoice ||
            pricingState.above !== pricing.above ||
            pricingState.back !== pricing.back ||
            pricingState.profit !== pricing.profit
        )
    }

    const updatePricingFunc = (isPurchase) => {
        if (hasPricingChanged()) {
            setIsUpdating(true)
            let updateData = pricingState
            updateData.id = pricing.id
            return updatePricing(pricing.id, updateData).then((data) => {
                setIsUpdating(false)
                fetchDesking(isPurchase)
            }).catch(() => {
                setIsUpdating(false)
            })
        } else {
            fetchDesking(isPurchase)
        }
    }

    const createNewPricing = (isPurchase) => {
        return createPricing(pricingState).then((data) => {
            fetchDesking(isPurchase)
            setPricingState(
                getInitialState(null, trim, quoteSettings)
            )
            fetchPricings(params.make)
        })
    }

    const fetchDesking = (isPurchase) => {
        let createData = structuredClone(pricingState)
        createData['is_purchase'] = isPurchase

        let mileage = createData.mileage
        if (['Ford', 'Lincoln'].includes(params.make)) {
            mileage = createData.mileage_alt
        }

        openModal({
            element: DeskingModal,
            id: "DeskingModal",
            props: {
                onSave: (updateData) =>  {
                    openModal({
                        element: DealModal,
                        id: "DealModal",
                        props: {id: updateData.id}
                    })
                },
                isDeskingContext: false,
                ...createData,
                id: null,
                mileage: mileage
            }
        })
    }

    const buildId = (field) => {
        return pricing ? `PricingListRow-${field}-${pricing.id}` : `PricingListRow-create-${field}`
    }

    const isLoading = createDealResponse?.loading || createPricingResponse?.loading || updatePricingResponse?.loading
    const isDisabled = Boolean(!pricingState.msrp || isLoading)

    const openLocationModal = () => {
        openModal({
            element: LocationModal,
            id: "LocationModal",
            props: {
                initialLocation: dealLocation,
                selectLocation: saveLocation,
            }
        })
    }

    useEnterEffect(() => {
        const docId = document.activeElement.id
        const [_, action, activePricingId] = docId.split('-')

        if (!isDisabled) {
            if (activePricingId === (String(pricing?.id)) ) {
                updatePricingFunc(false)
            }
            if (
                action === 'create' &&
                Boolean(!pricing)
            ) {
                createNewPricing(false)
            }
        }
    }, [pricingState])

    useDidMountEffect(function updatePricingStateWhenQuoteSettingsChange(){
        if (quoteSettings) {
            const customerLocation = quoteSettings.customer_location
            const dealerLocation = quoteSettings.dealer_location
            setPricingState({
                ...pricingState,
                profit: pricingState?.profit || quoteSettings.profit,
                drive_off: quoteSettings.drive_off,
                mileage: quoteSettings.mileage,
                mileage_alt: quoteSettings.mileage_alt,

                location: quoteSettings.customer_location,
                location_id: quoteSettings.customer_location.id,
                address_line_one: customerLocation.address_line_one,
                address_line_two: customerLocation.address_line_two,
                city: customerLocation.city,
                state: customerLocation.state,
                zipcode: customerLocation.zipcode,
                county: customerLocation.county,

                dealer_location: quoteSettings.dealer_location,
                dealer_location_id: quoteSettings.dealer_location.id,
                dealer_address_line_one: dealerLocation.address_line_one,
                dealer_address_line_two: dealerLocation.address_line_two,
                dealer_city: dealerLocation.city,
                dealer_state: dealerLocation.state,
                dealer_zipcode: dealerLocation.zipcode,
                dealer_county: dealerLocation.county
            })
        }
    }, [quoteSettings])

    const isDefaultLocation = Boolean(dealLocation.id === default_location.id)
    const isCreating = isCreatingLease || isCreatingPurchase

    return (
        <React.Fragment
            key={pricing?.id || `${trim.id}-pricing-new`}
        >
            {
                pricing?.id &&
                <td className={'delete-pricing-row-icon'}>
                    <Tooltip title="Delete" arrow placement={'top'}>
                        <DeleteSharpIcon
                            className={classnames(
                                'action-icon garbage', {
                                    loader: isDeleting
                                }
                            )}
                            onClick={() => deletePricingRow(pricing.id)}
                        />
                    </Tooltip>
                </td>
            }
            <td className={'pricing-list-row_description'}>
                <input
                    id={buildId('description')}
                    type="text"
                    className="note-input"
                    placeholder="Note"
                    value={pricingState.description}
                    onChange={(e) => updatePricingState('description', e.target.value)}
                />
            </td>
            <td>
                <CustomCurrencyInput
                    id={buildId('msrp')}
                    value={pricingState.msrp}
                    onChange={(value) => updatePricingState('msrp', value)}
                    prefix="$"
                    placeholder={'0'}
                    className={classnames('payment-row_value', {
                        loading: isCreating
                    })}
                />
            </td>
            <td>
                <CustomCurrencyInput
                    id={buildId('invoice')}
                    prefix="$"
                    value={pricingState.invoice}
                    onChange={(value) => updatePricingState('invoice', value)}
                    allowNull={true}
                    className={classnames('payment-row_value', {
                        loading: isCreating
                    })}
                />
            </td>
            <td>
                <CustomCurrencyInput
                    id={buildId('above')}
                    prefix="$"
                    value={pricingState.above}
                    defaultValue={0}
                    onChange={(value) => updatePricingState('above', value)}
                    className={classnames('payment-row_value', {
                        loading: isCreating
                    })}
                />
            </td>
            <td>
                <CustomCurrencyInput
                    id={buildId('back')}
                    prefix="$"
                    value={pricingState.back}
                    defaultValue={0}
                    onChange={(value) => updatePricingState('back', value)}
                    className={classnames('payment-row_value', {
                        loading: isCreating
                    })}
                />
            </td>
            <td>
                <CustomCurrencyInput
                    id={buildId('profit')}
                    prefix="$"
                    value={pricingState.profit}
                    defaultValue={0}
                    onChange={(value) => updatePricingState('profit', value)}
                    className={classnames('payment-row_value', {
                        loading: isCreating
                    })}
                />
            </td>
            <td>
                <Tooltip title={'Lease'} arrow placement={'top'}>
                    <AddOutlinedIcon
                        className={classnames(
                            'action-icon', {
                                loader: isCreatingLease,
                                disabled: isDisabled
                            }
                        )}
                        onClick={() => {
                            if (isLoading) return;
                            if (pricing?.id) {
                                updatePricingFunc(false)
                            } else {
                                createNewPricing(false)
                            }
                        }}
                    />
                </Tooltip>
                <Tooltip title={'Purchase'} arrow placement={'top'}>
                    <AccountBalanceIcon
                        className={classnames(
                            'action-icon purchase', {
                                loader: isCreatingPurchase,
                                disabled: isDisabled
                            }
                        )}
                        onClick={() => {
                            if (isLoading) return;
                            if (pricing?.id) {
                                updatePricingFunc(true)
                            } else {
                                createNewPricing(true)
                            }
                        }}
                    />
                </Tooltip>

                <Tooltip
                    title={`Edit Location (${buildLocationString(dealLocation)})`}
                    arrow placement={'top'}
                >
                    {
                        isDefaultLocation ?
                            <LocationOnIcon
                                className={'action-icon'}
                                onClick={openLocationModal}
                            /> :
                            <EditLocationIcon
                                className={'action-icon'}
                                onClick={openLocationModal}
                            />
                    }
                </Tooltip>
                {
                    // Existing Pricing Section
                    pricing?.id &&
                    <Tooltip title={'Quotes'} arrow placement={'top'}>
                        <Link to={`/quotes?pricing_id=${pricing.id}`} target="_blank">
                            <LibraryBooksIcon
                                className={'action-icon'}
                            />
                        </Link>
                    </Tooltip>

                }
            </td>
        </React.Fragment>
    )
}